import { Component, computed, inject, signal, TemplateRef, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Reservation, ReservationInvoice } from '@appTypes/api.types';
import { LocaleService } from '@core/services/locale.service';
import { TranslatePipe } from '@ngx-translate/core';
import { dispatch, select } from '@ngxs/store';
import { reservationsDownloadDocument } from '@pages/reservations-page/state/reservations.action';
import { ReservationsState } from '@pages/reservations-page/state/reservations.state';
import { ButtonLoadingDirective } from '@shared/directives/button-loading.directive';
import { DynamicModalData, OnDynamicModal } from '@shared/modules/dynamic-modal/dynamic-modal.types';
import { PriceFormatPipe } from '@shared/pipes/price-format.pipe';
import { NextPaymentComponent } from '../next-payment/next-payment.component';

/**
 * My reservations ▸ Show Prices. The totals are the API's (product invoices R-PI28); the footer offers one **Download Invoice** per live agency invoice
 * the API lists (R-PI29: a hotel + cruise reservation has two, both numbered as the reservation's first - told apart by the amount), or - without
 * that list - the single button of rule R-3 as before. QA doc: docs processes/product-invoices.md R-PI28 / R-PI29; features/website/reservations-list.md R-3.
 */
@Component({
  selector: 'app-reservation-price-details',
  imports: [TranslatePipe, PriceFormatPipe, MatButtonModule, ButtonLoadingDirective, NextPaymentComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './reservation-price-details.component.html'
})
export class ReservationPriceDetailsComponent implements OnDynamicModal<Reservation> {
  readonly #localeService = inject(LocaleService);

  readonly modalFooterTemplateRef = viewChild.required<TemplateRef<unknown>>('modalFooterTemplateRef');
  readonly modalTitle = signal('Reservation Price Details');

  readonly #onDownloadInvoice = dispatch(reservationsDownloadDocument);

  readonly downloadDocumentLoadState = select(ReservationsState.downloadDocumentLoadState);

  /** The link being downloaded right now (its button shows the spinner, the other invoices wait). */
  readonly downloadingUrl = computed<string | null>(() => {
    const loadState = this.downloadDocumentLoadState();
    return loadState.status === 'loading'
      ? ((loadState.additionlData as { url?: string } | undefined)?.url ?? null)
      : null;
  });

  public reservation!: Reservation;

  /** R-PI29: the live agency invoices with a download link; empty = the single Download Invoice of rule R-3. */
  public invoices: ReservationInvoice[] = [];

  public onDynamicModalClose!: () => void;

  public onDynamicModal(data: DynamicModalData<Reservation>): void {
    this.reservation = data;
    this.invoices = (data.invoices ?? []).filter((invoice) => !!invoice.url);
    this.modalTitle.set(this.#localeService.getWord('Application Price') + ' №' + this.reservation.number);
  }

  /** `reservation-download-invoice-<invoice id>` (C16: a stable suffix from data - the numbers repeat, the ids do not). */
  public invoiceTestId(invoice: ReservationInvoice): string {
    return 'reservation-download-invoice-' + String(invoice.id ?? invoice.number ?? '').toLowerCase();
  }

  public onDownloadInvoice() {
    this.#onDownloadInvoice(this.reservation.invoiceUrl!, this.#invoiceFileName(), true);
  }

  /** R-PI29: downloads THIS invoice; with several, the file names carry their position (the invoice numbers are the same). */
  public onDownloadInvoiceOf(invoice: ReservationInvoice, index: number) {
    if (!invoice.url) return;
    this.#onDownloadInvoice(invoice.url, this.#invoiceFileName(this.invoices.length > 1 ? index + 1 : null), true);
  }

  #invoiceFileName(position: number | null = null): string {
    const country = this.reservation.arrivalCountryName?.trim().replace(/\s+/g, '-');
    return ['Invoice', country, this.reservation.number, position].filter(Boolean).join('_') + '.pdf';
  }
}
